'use client';

import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { SheetPage, useResponsiveSheet } from '@church/nextjs-ui/components/ResponsiveSheet';
import {
  SEE_JESUS,
  SJ_TITLE,
  SeeJesusButton,
  SeeJesusCorner,
  SeeJesusFrame,
} from '@church/nextjs-ui/website';
import type { CampaignCommitment, CampaignCommitmentPatch, Commitment, Person } from '@/lib/types';
import { isUnauthorized } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { useProfile } from './ProfileContext';
import { Avatar } from './Avatar';
import { CARD, CTA_SMALL, DISPLAY, FIELD, Money, PageBody, Pii } from './ui';

/**
 * Was the campaign running in this year? Campaign start and end bound it;
 * an open end (See Jesus has none yet) runs on. Giving is shown by year, so
 * a commitment only appears beside the years it was part of.
 */
export function campaignInYear(
  c: { campaignStart: string | null; campaignEnd: string | null },
  year: number
): boolean {
  const from = c.campaignStart ? Number(c.campaignStart.slice(0, 4)) : null;
  const to = c.campaignEnd ? Number(c.campaignEnd.slice(0, 4)) : null;
  if (from !== null && year < from) return false;
  if (to !== null && year > to) return false;
  return true;
}

export interface CommitmentsState {
  pledges: Commitment[];
  cards: CampaignCommitment[];
  /** Loaded, and at least one commitment of either kind */
  hasAny: boolean;
  /** The commitments that belong to a year: the campaign was running then */
  forYear: (year: number) => { cards: CampaignCommitment[]; standalone: Commitment[] };
  loading: boolean;
  error: boolean;
  /** Pledges not linked to a campaign card */
  standalone: Commitment[];
  isSeeJesus: (campaignId: number) => boolean;
  anySeeJesus: boolean;
  save: (card: CampaignCommitment, patch: CampaignCommitmentPatch) => void;
  remove: (card: CampaignCommitment) => void;
  editing: CampaignCommitment | null;
  setEditing: (card: CampaignCommitment | null) => void;
}

/**
 * The commitments behind the Giving card: a campaign card
 * (`Pledge_Campaign_Commitments`, what a person filled in on a campaign
 * page, See Jesus today) and MP `Pledges` with what has come in against
 * them, as the old `mpp-my-pledges` widget showed. A pledge a card is
 * linked to rides along on the card instead of standing alone. Saving and
 * withdrawing are optimistic; a commitment is made on the campaign page,
 * not here.
 */
export function useCommitments(): CommitmentsState {
  const { api, run, seeJesusCampaignId } = useProfile();
  const [pledges, setPledges] = useState<Commitment[] | null | 'error'>(null);
  const [cards, setCards] = useState<CampaignCommitment[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .getCommitments(controller.signal)
      .then((c) => {
        if (controller.signal.aborted) return;
        setPledges(c.pledges);
        setCards(c.campaign);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setPledges(isUnauthorized(err) ? [] : 'error');
      });
    return () => controller.abort();
  }, [api]);

  const loaded = pledges !== null && pledges !== 'error';
  const isSeeJesus = (campaignId: number) => campaignId === seeJesusCampaignId;
  const linked = new Set(cards.map((c) => c.pledgeId).filter((id): id is number => id !== null));
  const standalone = loaded ? pledges.filter((p) => !linked.has(p.pledgeId)).sort(byLiveness) : [];
  const anySeeJesus =
    cards.some((c) => isSeeJesus(c.campaignId)) || standalone.some((p) => isSeeJesus(p.campaignId));

  function save(card: CampaignCommitment, patch: CampaignCommitmentPatch) {
    const projectedAnnual = round(patch.previousAnnual + patch.expandedAnnual);
    const projectedTwoYear = round(projectedAnnual * 2);
    const next: CampaignCommitment = {
      ...card,
      ...patch,
      projectedAnnual,
      projectedTwoYear,
      total: round(projectedTwoYear + patch.storedResources),
    };
    const before = cards;
    setCards((list) => list.map((c) => (c.commitmentId === card.commitmentId ? next : c)));
    void run(
      async () => {
        const saved = await api.updateCampaignCommitment(card.commitmentId, patch);
        // A save's echo carries no giving total; keep the one already read.
        setCards((list) =>
          list.map((c) =>
            c.commitmentId === card.commitmentId
              ? {
                  ...saved,
                  givenSinceCommitment: saved.givenSinceCommitment ?? c.givenSinceCommitment,
                }
              : c
          )
        );
      },
      () => setCards(before),
      'Commitment updated'
    );
  }

  function remove(card: CampaignCommitment) {
    const before = cards;
    setCards((list) => list.filter((c) => c.commitmentId !== card.commitmentId));
    void run(
      () => api.removeCampaignCommitment(card.commitmentId),
      () => setCards(before),
      'Commitment removed'
    );
  }

  return {
    pledges: loaded ? pledges : [],
    cards,
    hasAny: cards.length > 0 || standalone.length > 0,
    forYear: (year) => ({
      cards: cards.filter((c) => campaignInYear(c, year)),
      standalone: standalone.filter((p) => campaignInYear(p, year)),
    }),
    loading: pledges === null,
    error: pledges === 'error' && cards.length === 0,
    standalone,
    isSeeJesus,
    anySeeJesus,
    save,
    remove,
    editing: cards.find((c) => c.commitmentId === editingId) ?? null,
    setEditing: (card) => setEditingId(card ? card.commitmentId : null),
  };
}

/**
 * The commitments on the Giving card, under the year's chart. See Jesus
 * wears its brand here too, a small version of the sheet's card: the gold
 * frame, the blackletter title, the corner mark, the foil amount. Other
 * campaigns and pledges are a line each in the card's light ink.
 */
export function CommitmentsSummary({ c, year }: { c: CommitmentsState; year: number }) {
  const { cards, standalone } = c.forYear(year);
  if (cards.length === 0 && standalone.length === 0) return null;
  const sjCards = cards.filter((card) => c.isSeeJesus(card.campaignId));
  const otherCards = cards.filter((card) => !c.isSeeJesus(card.campaignId));
  const sjPledges = standalone.filter((p) => c.isSeeJesus(p.campaignId));
  const otherPledges = standalone.filter((p) => !c.isSeeJesus(p.campaignId));
  return (
    <div className="relative flex flex-col gap-2 border-t border-gray-100 pt-3">
      <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase">
        {cards.length + standalone.length === 1 ? 'Commitment' : 'Commitments'}
      </p>
      {sjCards.map((card) => (
        <SeeJesusMini
          key={`card-${card.commitmentId}`}
          amount={card.total}
          sub="Two-year commitment"
          given={givenSince(card)}
          pledge={
            card.pledgeId !== null
              ? (c.pledges.find((p) => p.pledgeId === card.pledgeId) ?? null)
              : null
          }
        />
      ))}
      {sjPledges.map((p) => (
        <SeeJesusMini key={`pledge-${p.pledgeId}`} amount={p.total} sub="Pledged" pledge={p} />
      ))}
      {(otherCards.length > 0 || otherPledges.length > 0) && (
        <ul className="flex flex-col gap-2">
          {otherCards.map((card) => (
            <li
              key={`card-${card.commitmentId}`}
              className="flex items-baseline justify-between gap-3"
            >
              <span className="min-w-0 truncate text-sm font-bold text-[#1c2b39]">
                {card.campaignName}
              </span>
              <span className="shrink-0 text-sm text-gray-600">
                <Money value={card.total} /> <span className="text-gray-400">over 2 years</span>
              </span>
            </li>
          ))}
          {otherPledges.map((p) => {
            const pct = p.total > 0 ? Math.min(100, Math.round((p.given / p.total) * 100)) : 0;
            return (
              <li key={`pledge-${p.pledgeId}`} className="flex flex-col gap-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-bold text-[#1c2b39]">
                    {p.campaignName}
                  </span>
                  <span className="shrink-0 text-sm text-gray-600">
                    <Money value={p.given} />
                    <span className="text-gray-400"> of </span>
                    <Money value={p.total} />
                  </span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200">
                  <div className="h-full rounded-full bg-[#299c8f]" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** See Jesus on the Giving card: the sheet's card in miniature. Tapping the card opens the sheet. */
function SeeJesusMini({
  amount,
  sub,
  given = null,
  pledge,
}: {
  amount: number;
  sub: string;
  /** Giving since the commitment, when there is no linked pledge to show instead. */
  given?: { amount: number; since: string } | null;
  pledge: Commitment | null;
}) {
  return (
    <SeeJesusFrame
      className="w-full"
      paneClassName="relative flex flex-col gap-2.5 p-4"
      corner={<SeeJesusCorner className="w-14" />}
    >
      <div className="pr-14">
        <h3 className={SJ_TITLE}>See Jesus</h3>
        <p className="mt-1 text-[10px] font-semibold tracking-widest text-[#f6efe9]/60 uppercase">
          {sub}
        </p>
      </div>
      <p className="sj-display text-4xl leading-none text-[#f6efe9]">
        <Money value={amount} />
      </p>
      {pledge ? (
        <Progress given={pledge.given} total={pledge.total} seeJesus />
      ) : (
        given && (
          <Progress
            given={given.amount}
            total={amount}
            seeJesus
            label={`given since ${fmtDate(given.since)}`}
          />
        )
      )}
    </SeeJesusFrame>
  );
}

/**
 * The commitments in the Giving sheet, every one as its full card, stacked:
 * a campaign card wears the campaign's brand and drills into Adjust (Back
 * returns here); a pledge shows what has come in against it.
 */
export function CommitmentsPage({ c, year }: { c: CommitmentsState; year: number }) {
  const { urls } = useProfile();
  const { navigate } = useResponsiveSheet();
  const { cards, standalone } = c.forYear(year);
  if (cards.length === 0 && standalone.length === 0) return null;
  const anySeeJesus =
    cards.some((x) => c.isSeeJesus(x.campaignId)) ||
    standalone.some((p) => c.isSeeJesus(p.campaignId));
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase">
        {anySeeJesus
          ? `Promised, running in ${year}, See Jesus first`
          : `Promised, running in ${year}`}
      </p>
      {cards.map((card) => (
        <div key={`card-${card.commitmentId}`} className="flex">
          <CampaignCard
            card={card}
            pledge={
              card.pledgeId !== null
                ? (c.pledges.find((p) => p.pledgeId === card.pledgeId) ?? null)
                : null
            }
            seeJesus={c.isSeeJesus(card.campaignId)}
            // Give means give: the giving page, never the campaign's own
            // page, See Jesus included. "About See Jesus" below is where
            // someone goes to read about the campaign.
            giveUrl={card.giveUrl ?? urls.give}
            onAdjust={() => {
              c.setEditing(card);
              navigate('adjust');
            }}
          />
        </div>
      ))}
      {standalone.map((p) => (
        <PledgeCard
          key={`pledge-${p.pledgeId}`}
          c={p}
          seeJesus={c.isSeeJesus(p.campaignId)}
          giveUrl={p.giveUrl ?? urls.give}
          className="w-full"
        />
      ))}
      {cards.some((card) => c.isSeeJesus(card.campaignId)) && (
        <a
          href={SEE_JESUS.campaignUrl}
          className="self-start text-sm font-bold text-[#299c8f] hover:underline"
        >
          About See Jesus
        </a>
      )}
    </div>
  );
}

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * What the household has given toward a campaign card since committing — the
 * See Jesus dashboard's count (General + Missions from the commitment date).
 * Shown where no staff-linked pledge carries its own "given so far".
 */
function givenSince(card: CampaignCommitment): { amount: number; since: string } | null {
  if (card.givenSinceCommitment == null || !card.commitmentDate) return null;
  return { amount: card.givenSinceCommitment, since: card.commitmentDate };
}

/** Active and pending first, then completed, then discontinued; newest campaign first within each. */
function byLiveness(a: Commitment, b: Commitment): number {
  const rank = (c: Commitment) =>
    c.status === 'active' ? 0 : c.status === 'pending' ? 1 : c.status === 'completed' ? 2 : 3;
  return rank(a) - rank(b) || (b.campaignStart ?? '').localeCompare(a.campaignStart ?? '');
}

/* ------------------------------------------------------------------ */
/* The campaign card: See Jesus in the campaign's brand, others in navy */
/* ------------------------------------------------------------------ */

function CampaignCard({
  card,
  pledge,
  seeJesus,
  giveUrl,
  onAdjust,
}: {
  card: CampaignCommitment;
  pledge: Commitment | null;
  seeJesus: boolean;
  giveUrl: string;
  onAdjust: () => void;
}) {
  const { data } = useProfile();
  const people = whose(card, [data.me, ...data.members]);
  const names = people.map((p) => p.nickname || p.firstName);
  const title = seeJesus ? 'See Jesus' : card.campaignName;
  const ink = seeJesus ? 'text-[#f6efe9]' : 'text-white';
  const muted = seeJesus ? 'text-[#f6efe9]/60' : 'text-white/60';
  const line = seeJesus ? 'border-[#c79854]/30' : 'border-white/15';

  const body = (
    <>
      <div className={seeJesus ? 'pr-20 sm:pr-24' : ''}>
        <h3 className={seeJesus ? SJ_TITLE : `${DISPLAY} text-3xl leading-none ${ink}`}>{title}</h3>
        <p className={`mt-1.5 text-[11px] font-semibold tracking-widest uppercase ${muted}`}>
          {names.length > 1 ? 'Our' : 'My'} two-year commitment
          {card.commitmentDate ? ` · ${fmtDate(card.commitmentDate)}` : ''}
        </p>
      </div>

      <div className="flex items-center gap-4">
        {people.length > 0 && (
          <span className="flex shrink-0 -space-x-2.5">
            {people.map((p) => (
              <span
                key={p.contactId}
                className={`rounded-full ring-2 ${seeJesus ? 'ring-[#c79854]' : 'ring-white/40'}`}
              >
                <Avatar name={p.displayName} imageUrl={p.imageUrl} size={44} />
              </span>
            ))}
          </span>
        )}
        <span
          className={`${seeJesus ? 'sj-display' : DISPLAY} min-w-0 text-5xl leading-none ${ink}`}
        >
          <Money value={card.total} />
        </span>
      </div>

      <dl className={`grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 border-t pt-4 text-sm ${line}`}>
        <Line label="Annual giving" value={card.previousAnnual} muted={muted} ink={ink} />
        <Line
          label={`+ ${seeJesus ? 'See Jesus' : 'Expanded'}`}
          value={card.expandedAnnual}
          muted={muted}
          ink={ink}
        />
        <Line label="= Per year" value={card.projectedAnnual} muted={muted} ink={ink} strong />
        <Line label="× 2 years" value={card.projectedTwoYear} muted={muted} ink={ink} />
        {card.storedResources > 0 && (
          <Line label="+ Stored resources" value={card.storedResources} muted={muted} ink={ink} />
        )}
      </dl>

      {pledge ? (
        <Progress given={pledge.given} total={pledge.total} seeJesus={seeJesus} />
      ) : (
        seeJesus &&
        givenSince(card) && (
          <Progress
            given={givenSince(card)!.amount}
            total={card.total}
            seeJesus
            label={`given since ${fmtDate(givenSince(card)!.since)}`}
          />
        )
      )}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-1">
        <button
          type="button"
          onClick={onAdjust}
          className={`inline-flex items-center gap-1 text-sm font-bold transition-all hover:scale-105 active:scale-95 ${
            seeJesus
              ? 'sj-display text-lg font-normal text-[#e8c998]'
              : 'text-white/80 hover:text-white'
          }`}
        >
          Adjust
          <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
        {seeJesus ? (
          <SeeJesusButton href={giveUrl} className="px-5 py-2 text-base">
            Give
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </SeeJesusButton>
        ) : (
          <a href={giveUrl} className={`${CTA_SMALL} bg-[#299c8f] text-white`}>
            Give
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </a>
        )}
      </div>
    </>
  );

  if (seeJesus) {
    return (
      <SeeJesusFrame
        className="flex w-full flex-col"
        paneClassName="relative flex flex-1 flex-col gap-5 p-5 sm:p-6"
        corner={<SeeJesusCorner />}
      >
        {body}
      </SeeJesusFrame>
    );
  }
  return (
    <div
      className={`${CARD} flex w-full flex-col gap-5 p-5 ring-0 sm:p-6`}
      style={{ background: 'linear-gradient(135deg, #1c2b39 0%, #243848 50%, #2d4a5c 100%)' }}
    >
      {body}
    </div>
  );
}

/** Whose commitment: the row's person, and the spouse when it was made for both. */
function whose(card: CampaignCommitment, everyone: Person[]): Person[] {
  const owner = everyone.find((p) => p.contactId === card.contactId);
  if (!owner) return [];
  if (!card.includesSpouse) return [owner];
  const spouse = everyone.find(
    (p) => p.contactId !== owner.contactId && p.householdPositionId === 1
  );
  return spouse ? [owner, spouse] : [owner];
}

function Line({
  label,
  value,
  muted,
  ink,
  strong = false,
}: {
  label: string;
  value: number;
  muted: string;
  ink: string;
  strong?: boolean;
}) {
  return (
    <>
      <dt className={`text-xs font-semibold tracking-wide uppercase ${strong ? ink : muted}`}>
        {label}
      </dt>
      <dd className={`text-right tabular-nums ${strong ? `font-bold ${ink}` : muted}`}>
        <Money value={value} />
      </dd>
    </>
  );
}

function Progress({
  given,
  total,
  seeJesus,
  label = 'given so far',
}: {
  given: number;
  total: number;
  seeJesus: boolean;
  label?: string;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((given / total) * 100)) : 0;
  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={`flex items-baseline justify-between text-xs ${seeJesus ? 'text-[#f6efe9]/70' : 'text-white/70'}`}
      >
        <span>
          <Money value={given} /> {label}
        </span>
        <span className={`font-bold ${seeJesus ? 'text-[#e8c998]' : 'text-white'}`}>{pct}%</span>
      </div>
      <div
        className={`h-1.5 w-full overflow-hidden rounded-full ${seeJesus ? 'bg-white/10' : 'bg-white/15'}`}
      >
        <div
          className={`h-full rounded-full ${seeJesus ? 'sj-foil' : 'bg-[#299c8f]'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* A pledge on its own: the stock widget's card, See Jesus when it is  */
/* ------------------------------------------------------------------ */

function PledgeCard({
  c,
  seeJesus,
  giveUrl,
  className = '',
}: {
  c: Commitment;
  seeJesus: boolean;
  giveUrl: string;
  className?: string;
}) {
  const live = c.status === 'active' || c.status === 'pending';
  const pct = c.total > 0 ? Math.min(100, Math.round((c.given / c.total) * 100)) : 0;
  const done = c.status === 'completed' || (c.total > 0 && c.given >= c.total);
  const schedule =
    c.installmentsPlanned && c.installmentsPlanned > 1
      ? `${c.installmentsPlanned} ${c.installmentsPerYear === 12 ? 'monthly' : c.installmentsPerYear === 52 ? 'weekly' : c.installmentsPerYear === 1 ? 'yearly' : ''} gifts`.replace(
          /\s+/g,
          ' '
        )
      : c.installmentsPlanned === 1
        ? 'One gift'
        : null;
  const ink = seeJesus ? 'text-[#f6efe9]' : 'text-[#1c2b39]';
  const muted = seeJesus ? 'text-[#f6efe9]/60' : 'text-gray-500';
  const body = (
    <>
      <div className={`flex items-start justify-between gap-3 ${seeJesus ? 'pr-20 sm:pr-24' : ''}`}>
        <span className="min-w-0">
          <span
            className={
              seeJesus ? `${SJ_TITLE} block` : `${DISPLAY} block text-2xl leading-none ${ink}`
            }
          >
            {seeJesus ? 'See Jesus' : c.campaignName}
          </span>
          <span className={`pii mt-1 block text-xs ${muted}`}>
            {[
              c.beneficiary ? `for ${c.beneficiary}` : null,
              c.campaignEnd ? `through ${fmtDate(c.campaignEnd)}` : null,
            ]
              .filter(Boolean)
              .join(' · ') || (c.campaignStart ? `since ${fmtDate(c.campaignStart)}` : '')}
          </span>
        </span>
        {!seeJesus && (
          <span
            className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide uppercase ${
              done
                ? 'bg-[#299c8f] text-white'
                : live
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-gray-100 text-gray-500'
            }`}
          >
            {done ? 'Fulfilled' : c.statusLabel}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-end justify-between gap-3">
          <span>
            <span
              className={`${seeJesus ? 'sj-display' : DISPLAY} block text-4xl leading-none ${ink}`}
            >
              <Money value={c.given} />
            </span>
            <span className={`block text-xs ${muted}`}>
              given of <Money value={c.total} />
            </span>
          </span>
          <span
            className={`text-sm font-bold tabular-nums ${seeJesus ? 'text-[#e8c998]' : 'text-[#299c8f]'}`}
          >
            {pct}%
          </span>
        </div>
        <div
          className={`h-2 w-full overflow-hidden rounded-full ${seeJesus ? 'bg-white/10' : 'bg-gray-100'}`}
        >
          <div
            className={`h-full rounded-full ${seeJesus ? 'sj-foil' : done ? 'bg-[#299c8f]' : 'bg-[#1c2b39]'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {(schedule || c.firstInstallmentDate) && (
          <span className={`text-xs ${muted}`}>
            {[schedule, c.firstInstallmentDate ? `from ${fmtDate(c.firstInstallmentDate)}` : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
      </div>

      {live && c.remaining > 0 && (
        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 text-sm">
          <span className={muted}>
            <Money value={c.remaining} /> to go
          </span>
          {seeJesus ? (
            <SeeJesusButton href={giveUrl} className="px-5 py-2 text-base">
              Give
              <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
            </SeeJesusButton>
          ) : (
            <a href={giveUrl} className={`${CTA_SMALL} bg-[#299c8f] text-white`}>
              Give
              <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
            </a>
          )}
        </div>
      )}
    </>
  );
  if (seeJesus) {
    return (
      <div className={`${className} flex`}>
        <SeeJesusFrame
          className="flex w-full flex-col"
          paneClassName="relative flex flex-1 flex-col gap-4 p-5"
          corner={<SeeJesusCorner />}
        >
          {body}
        </SeeJesusFrame>
      </div>
    );
  }
  return (
    <div className={`${CARD} flex flex-col gap-4 p-5 ${live ? '' : 'opacity-80'} ${className}`}>
      {body}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The adjust sheet: the campaign page's arithmetic, on smoked glass   */
/* ------------------------------------------------------------------ */

/**
 * The Adjust page of the Giving sheet: the three money fields, live
 * totals, Save (back to the list) and a two-step Withdraw. The See Jesus
 * card keeps its foil title and gold Save here.
 */
export function AdjustPage({
  card,
  seeJesus,
  onSave,
  onRemove,
}: {
  card: CampaignCommitment;
  seeJesus: boolean;
  onSave: (patch: CampaignCommitmentPatch) => void;
  onRemove: () => void;
}) {
  const { goBack } = useResponsiveSheet();
  const [previous, setPrevious] = useState(moneyInput(card.previousAnnual));
  const [expanded, setExpanded] = useState(moneyInput(card.expandedAnnual));
  const [stored, setStored] = useState(moneyInput(card.storedResources));
  const [confirmRemove, setConfirmRemove] = useState(false);

  const previousNum = parseMoney(previous);
  const expandedNum = parseMoney(expanded);
  const storedNum = parseMoney(stored);
  const annual = round(previousNum + expandedNum);
  const twoYear = round(annual * 2);
  const total = round(twoYear + storedNum);
  const changed =
    previousNum !== card.previousAnnual ||
    expandedNum !== card.expandedAnnual ||
    storedNum !== card.storedResources;
  const ink = 'text-[#1c2b39]';

  return (
    <PageBody>
      <div>
        <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase">
          {seeJesus ? 'See Jesus' : card.campaignName}
        </p>
        <h3
          className={
            seeJesus
              ? 'sj-foil-text sj-display mt-1 text-4xl leading-none'
              : `${DISPLAY} mt-1 text-3xl leading-none ${ink}`
          }
        >
          Adjust your commitment
        </h3>
      </div>
      <MoneyField
        label="Annual giving"
        hint="What you already give in a year"
        value={previous}
        onChange={setPrevious}
      />
      <MoneyField
        label={seeJesus ? 'See Jesus' : 'Expanded giving'}
        hint="The stretch beyond that, per year"
        value={expanded}
        onChange={setExpanded}
      />
      <MoneyField
        label="Stored resources"
        hint="A one-time gift from savings, stock, property"
        value={stored}
        onChange={setStored}
      />
      <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 border-t border-gray-200 pt-4 text-sm">
        <Line label="= Per year" value={annual} muted="text-gray-500" ink={ink} />
        <Line label="× 2 years" value={twoYear} muted="text-gray-500" ink={ink} />
        {storedNum > 0 && (
          <Line label="+ Stored resources" value={storedNum} muted="text-gray-500" ink={ink} />
        )}
      </div>
      <div className="flex items-end justify-between gap-4">
        <span className="text-[11px] font-bold tracking-widest text-gray-500 uppercase">
          Two-year commitment
        </span>
        <span
          className={`${seeJesus ? 'sj-foil-text sj-display' : `${DISPLAY} ${ink}`} text-5xl leading-none`}
        >
          <Money value={total} />
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
        {confirmRemove ? (
          <span className="flex items-center gap-3 text-sm">
            <span className="text-gray-500">Withdraw it?</span>
            <button
              type="button"
              onClick={() => {
                onRemove();
                goBack();
              }}
              className="font-bold text-red-600 transition-colors hover:text-red-700"
            >
              Yes, withdraw
            </button>
            <button
              type="button"
              onClick={() => setConfirmRemove(false)}
              className="text-gray-500 hover:text-[#1c2b39]"
            >
              Keep it
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmRemove(true)}
            className="text-sm font-semibold text-gray-500 transition-colors hover:text-red-600"
          >
            Withdraw this commitment
          </button>
        )}
        <button
          type="button"
          disabled={!changed}
          onClick={() => {
            onSave({
              previousAnnual: previousNum,
              expandedAnnual: expandedNum,
              storedResources: storedNum,
            });
            goBack();
          }}
          className={
            seeJesus
              ? 'sj-foil sj-foil-btn inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-lg leading-none transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100'
              : `${CTA_SMALL} bg-[#299c8f] text-white disabled:opacity-40`
          }
        >
          Save
          <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
    </PageBody>
  );
}

function MoneyField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">{label}</span>
        <span className="text-xs text-gray-500">{hint}</span>
      </span>
      <span className="relative mt-2 block">
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-lg font-bold text-gray-400"
        >
          $
        </span>
        <input
          inputMode="decimal"
          value={value}
          placeholder="0"
          onChange={(e) => onChange(sanitizeMoney(e.target.value))}
          className={`${FIELD} pl-9 text-right text-xl font-bold tabular-nums`}
        />
      </span>
    </label>
  );
}

const moneyInput = (n: number) => (n > 0 ? String(n) : '');
const sanitizeMoney = (raw: string) =>
  raw
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*)\./g, '$1')
    .replace(/^(\d*\.\d{0,2}).*$/, '$1');
const parseMoney = (raw: string) => {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? round(n) : 0;
};
